import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../services/authService';

export default function EditProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with current user data
  useEffect(() => {
    if (user) {
      const currentName = user.user_metadata?.name || user.email?.split('@')[0] || '';
      const currentEmail = user.email || '';
      setName(currentName);
      setEmail(currentEmail);
    }
  }, [user]);

  // Track if there are changes
  useEffect(() => {
    if (!user) return;
    
    const currentName = user.user_metadata?.name || user.email?.split('@')[0] || '';
    const currentEmail = user.email || '';
    
    const changed = name !== currentName || email !== currentEmail;
    setHasChanges(changed);
  }, [name, email, user]);

  const validateName = (value: string): boolean => {
    if (!value || value.trim().length < 2) {
      setNameError('Nama minimal 2 karakter');
      return false;
    }
    if (value.trim().length > 50) {
      setNameError('Nama maksimal 50 karakter');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value || !emailRegex.test(value)) {
      setEmailError('Email tidak valid');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (nameError) validateName(value);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) validateEmail(value);
  };

  const handleSave = async () => {
    setError('');
    setSuccess(false);

    // Validate all fields
    const isNameValid = validateName(name);
    const isEmailValid = validateEmail(email);

    if (!isNameValid || !isEmailValid) {
      return;
    }

    setLoading(true);

    try {
      const updateData: { name?: string; email?: string } = {};
      
      const currentName = user?.user_metadata?.name || user?.email?.split('@')[0] || '';
      const currentEmail = user?.email || '';

      if (name !== currentName) {
        updateData.name = name.trim();
      }

      if (email !== currentEmail) {
        updateData.email = email.trim();
      }

      // Only send request if there are actual changes
      if (Object.keys(updateData).length > 0) {
        await updateUserProfile(updateData);
        
        setSuccess(true);
        
        // Show success message and navigate back after delay
        setTimeout(() => {
          navigate('/profile');
        }, 1500);
      } else {
        navigate('/profile');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal mengubah profil. Silakan coba lagi');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  return (
    <div
      className="min-h-screen bg-[#ffffeb]"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Header */}
      <div
        className="bg-[#3cbe8c] text-white px-6 py-4 sticky top-0 z-10 border-b-4 border-[#077f5d]"
        style={{ boxShadow: '0 4px 0 #077f5d' }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-[#2da877] border-2 border-[#077f5d]"
            style={{
              boxShadow: '2px 2px 0 #077f5d',
              transition: 'none'
            }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1
            className="text-xl font-bold flex-1 uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '14px',
              lineHeight: '1.4'
            }}
          >
            Edit Profil
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-6 py-6 pb-24 lg:pb-8">
        {/* Error Message */}
        {error && (
          <div
            className="mb-4 bg-red-50 border-2 border-red-600 text-red-700 px-4 py-3 font-mono"
            style={{ boxShadow: '3px 3px 0 #dc2626' }}
          >
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div
            className="mb-4 bg-[#dffbd0] border-2 border-[#077f5d] text-[#077f5d] px-4 py-3 font-mono"
            style={{ boxShadow: '3px 3px 0 #077f5d' }}
          >
            <p className="text-sm font-bold">✓ Profil berhasil diperbarui</p>
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {/* Name Field */}
          <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
            <label
              className="block text-[#077f5d] mb-2 uppercase"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '10px',
                fontWeight: 700
              }}
            >
              Nama
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              className={`w-full px-4 py-3 border-2 outline-none font-mono ${
                nameError
                  ? 'border-red-600 bg-red-50'
                  : 'border-[#077f5d] bg-white'
              }`}
              style={{
                boxShadow: nameError ? '2px 2px 0 #dc2626' : '2px 2px 0 #077f5d',
                transition: 'none'
              }}
              placeholder="Masukkan nama Anda"
              disabled={loading}
            />
            {nameError && (
              <p className="mt-2 text-sm text-red-600 font-mono">{nameError}</p>
            )}
          </div>

          {/* Email Field */}
          <div className="bg-white border-2 border-[#077f5d] p-4" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
            <label
              className="block text-[#077f5d] mb-2 uppercase"
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '10px',
                fontWeight: 700
              }}
            >
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={`w-full px-4 py-3 border-2 outline-none font-mono ${
                emailError
                  ? 'border-red-600 bg-red-50'
                  : 'border-[#077f5d] bg-white'
              }`}
              style={{
                boxShadow: emailError ? '2px 2px 0 #dc2626' : '2px 2px 0 #077f5d',
                transition: 'none'
              }}
              placeholder="email@example.com"
              disabled={loading}
            />
            {emailError && (
              <p className="mt-2 text-sm text-red-600 font-mono">{emailError}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 space-y-3">
          <button
            onClick={handleSave}
            disabled={loading || !hasChanges || !!nameError || !!emailError}
            className="w-full bg-[#3cbe8c] text-white font-bold py-3 px-6 border-2 border-[#077f5d] hover:bg-[#2da877] disabled:opacity-50 disabled:cursor-not-allowed uppercase flex items-center justify-center gap-2"
            style={{
              boxShadow: '4px 4px 0 #077f5d',
              transition: 'none',
              fontFamily: "'IBM Plex Mono', monospace"
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Simpan
              </>
            )}
          </button>

          <button
            onClick={handleCancel}
            disabled={loading}
            className="w-full bg-white text-[#1a1a1a] font-bold py-3 px-6 border-2 border-[#077f5d] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              boxShadow: '4px 4px 0 #077f5d',
              transition: 'none',
              fontFamily: "'IBM Plex Mono', monospace"
            }}
          >
            Batal
          </button>
        </div>
      </div>
    </div>
  );
}
